import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { ADMIN_SESSION_STORAGE_KEY, apiFetch, readApiJson } from '../lib/api';
import { fetchAttempts, syncAttempts } from '../services/cloudAttempts';
import {
  getHistory, mergeHistory, migrateGuestHistoryToUser, setActiveStorageUser,
  takeGuestPreparationPlanInputs
} from '../utils/storage';
import { hasFullAccess as evaluateFullAccess } from '../constants/access';
import {
  DEFAULT_PREPARATION_PREFERENCES,
  normalizePreparationPreferences
} from '../data/preparationProfiles';
import { planBookingAction } from '../utils/assessmentBooking';

const AuthContext = createContext(null);

const authRedirect = (path) => `${window.location.origin}${path}`;

const configurationError = () => ({
  message: 'CBAT Academy authentication is not configured on this device.'
});

const defaultProfile = (currentUser) => ({
  display_name: currentUser?.user_metadata?.display_name || '',
  email: currentUser?.email || '',
  preparation_preferences: DEFAULT_PREPARATION_PREFERENCES,
  assessment_booking: null,
  assessment_followup: null,
  // true/false once the server has said; null until then, which never
  // shows the Terms acceptance window.
  accepted_terms_privacy: null,
  accepted_terms_privacy_at: null
});

const localAdminFallback = (currentUser) => {
  const role = String(currentUser?.app_metadata?.role || '').toLowerCase();
  return role === 'admin';
};

// Set while a signed-in account has full access, for the static public pages
// (public/static-public.js): they can't check access, so their footer reads
// this to leave out Pricing. Cosmetic only.
export const FULL_ACCESS_HINT_KEY = 'cbat_full_access';

const PROFILE_CACHE_PREFIX = 'cbat_profile_cache';
const ACCESS_CACHE_PREFIX = 'cbat_access_cache';
const SITE_SETTINGS_CACHE_KEY = 'cbat_site_settings_cache';
const SITE_SETTINGS_CACHE_TTL_MS = 60 * 1000;
// Access state (subscription/entitlement) is granted server-side -- most
// often outside the user's own action, e.g. an admin manually granting
// access. Without a TTL, a tab that cached "no access" before the grant
// would keep reading that stale denial from sessionStorage forever (surviving
// reloads and re-navigation), since only closing the tab or the explicit
// "Refresh Access" button would ever re-check the server.
const ACCESS_CACHE_TTL_MS = 60 * 1000;
const RECOVERY_MODE_KEY = 'cbat_recovery_mode';
const ACTIVITY_HEARTBEAT_MS = 5 * 60 * 1000;

const urlIndicatesRecovery = () => {
  if (typeof window === 'undefined') return false;
  const searchParams = new URLSearchParams(window.location.search || '');
  const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  return searchParams.get('type') === 'recovery' || hashParams.get('type') === 'recovery';
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [accessState, setAccessState] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const [siteSettingsLoading, setSiteSettingsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(RECOVERY_MODE_KEY) === 'true' || urlIndicatesRecovery();
  });
  const [syncStatus, setSyncStatus] = useState('idle');
  const [syncError, setSyncError] = useState(null);
  const lastSyncedUserId = useRef(null);
  const lastProfileUserId = useRef(null);
  const lastAccessUserId = useRef(null);
  const lastSiteSettingsUserId = useRef(null);
  const preparedUserId = useRef(null);

  const readProfileCache = useCallback((userId) => {
    try {
      const raw = sessionStorage.getItem(`${PROFILE_CACHE_PREFIX}:${userId}`);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }, []);

  const writeProfileCache = useCallback((userId, data) => {
    try {
      sessionStorage.setItem(`${PROFILE_CACHE_PREFIX}:${userId}`, JSON.stringify(data));
    } catch (error) {
      // ignore cache write issues
    }
  }, []);

  const readAccessCache = useCallback((userId) => {
    try {
      const raw = sessionStorage.getItem(`${ACCESS_CACHE_PREFIX}:${userId}`);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.cachedAt || !parsed?.data) return null;
      if (Date.now() - parsed.cachedAt > ACCESS_CACHE_TTL_MS) {
        sessionStorage.removeItem(`${ACCESS_CACHE_PREFIX}:${userId}`);
        return null;
      }
      return parsed.data;
    } catch (error) {
      return null;
    }
  }, []);

  const writeAccessCache = useCallback((userId, data) => {
    try {
      sessionStorage.setItem(`${ACCESS_CACHE_PREFIX}:${userId}`, JSON.stringify({
        cachedAt: Date.now(),
        data
      }));
    } catch (error) {
      // ignore cache write issues
    }
  }, []);

  const readSiteSettingsCache = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(SITE_SETTINGS_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.cachedAt || !parsed?.data) return null;
      if (Date.now() - parsed.cachedAt > SITE_SETTINGS_CACHE_TTL_MS) {
        sessionStorage.removeItem(SITE_SETTINGS_CACHE_KEY);
        return null;
      }
      return parsed.data;
    } catch (error) {
      return null;
    }
  }, []);

  const writeSiteSettingsCache = useCallback((data) => {
    try {
      sessionStorage.setItem(SITE_SETTINGS_CACHE_KEY, JSON.stringify({
        cachedAt: Date.now(),
        data
      }));
    } catch (error) {
      // ignore cache write issues
    }
  }, []);

  const setRecoveryMode = useCallback((active) => {
    setIsRecoveryMode(active);
    if (typeof window === 'undefined') return;
    if (active) {
      sessionStorage.setItem(RECOVERY_MODE_KEY, 'true');
    } else {
      sessionStorage.removeItem(RECOVERY_MODE_KEY);
    }
  }, []);

  const loadBackendProfile = useCallback(async (currentUser, { force = false } = {}) => {
    if (!isSupabaseConfigured || !supabase || !currentUser?.id) return;

    // A profile cached before the Terms acceptance record existed is
    // refetched, so the acceptance window can't be missed or shown wrongly.
    const cached = !force ? readProfileCache(currentUser.id) : null;
    if (cached && typeof cached.profile?.accepted_terms_privacy === 'boolean') {
      const cachedProfile = cached.profile || {};
      setIsAdmin(Boolean(cached.isAdmin) || localAdminFallback(currentUser));
      setProfile({
        ...defaultProfile(currentUser),
        ...cachedProfile,
        preparation_preferences: normalizePreparationPreferences(
          cachedProfile.preparation_preferences,
          cachedProfile
        ),
        assessment_booking: cachedProfile.assessment_booking ?? null,
        assessment_followup: cachedProfile.assessment_followup ?? null
      });
      lastProfileUserId.current = currentUser.id;
      return;
    }

    setProfileLoading(true);
    try {
      const response = await apiFetch('/account/profile');
      if (!response.ok) {
        if (lastProfileUserId.current !== currentUser.id) {
          setIsAdmin(localAdminFallback(currentUser));
        }
        return;
      }
      const data = await response.json();
      const nextIsAdmin = Boolean(data?.is_admin) || localAdminFallback(currentUser);
      const nextProfile = {
        ...defaultProfile(currentUser),
        display_name: data?.display_name || defaultProfile(currentUser).display_name,
        username_changed_at: data?.username_changed_at || null,
        username_change_available: data?.username_change_available !== false,
        service_profile: data?.service_profile ?? null,
        target_role: data?.target_role ?? null,
        service_profile_set_at: data?.service_profile_set_at ?? null,
        preparation_preferences: normalizePreparationPreferences(
          data?.preparation_preferences,
          {
            service_profile: data?.service_profile,
            target_role: data?.target_role
          }
        ),
        assessment_booking: data?.assessment_booking ?? null,
        assessment_followup: data?.assessment_followup ?? null,
        accepted_terms_privacy: typeof data?.accepted_terms_privacy === 'boolean' ? data.accepted_terms_privacy : null,
        accepted_terms_privacy_at: data?.accepted_terms_privacy_at || null
      };
      setIsAdmin(nextIsAdmin);
      setProfile(nextProfile);
      writeProfileCache(currentUser.id, { isAdmin: nextIsAdmin, profile: nextProfile });
      lastProfileUserId.current = currentUser.id;
    } catch (error) {
      if (lastProfileUserId.current !== currentUser.id) {
        setIsAdmin(localAdminFallback(currentUser));
      }
    } finally {
      setProfileLoading(false);
    }
  }, [readProfileCache, writeProfileCache]);

  const loadAccessState = useCallback(async (currentUser, { force = false } = {}) => {
    if (!isSupabaseConfigured || !supabase || !currentUser?.id) return;

    const cached = !force ? readAccessCache(currentUser.id) : null;
    if (cached) {
      setAccessState(cached);
      lastAccessUserId.current = currentUser.id;
      return;
    }

    setAccessLoading(true);
    try {
      const response = await apiFetch('/account/access');
      if (!response.ok) {
        if (lastAccessUserId.current !== currentUser.id) {
          setAccessState(null);
        }
        return;
      }
      const data = await response.json();
      setAccessState(data);
      writeAccessCache(currentUser.id, data);
      lastAccessUserId.current = currentUser.id;
    } catch (error) {
      if (lastAccessUserId.current !== currentUser.id) {
        setAccessState(null);
      }
    } finally {
      setAccessLoading(false);
    }
  }, [readAccessCache, writeAccessCache]);

  const loadSiteSettings = useCallback(async (
    currentUser,
    { force = false, background = false } = {}
  ) => {
    if (!isSupabaseConfigured || !supabase || !currentUser?.id) return;

    const cached = !force ? readSiteSettingsCache() : null;
    if (cached) {
      setSiteSettings(cached);
      lastSiteSettingsUserId.current = currentUser.id;
      return;
    }

    if (!background) setSiteSettingsLoading(true);
    try {
      const response = await apiFetch('/site-settings');
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      setSiteSettings(data);
      writeSiteSettingsCache(data);
      lastSiteSettingsUserId.current = currentUser.id;
    } catch {
      // Keep the last known settings. A transient refresh failure must never
      // revoke access or replace an active route.
    } finally {
      if (!background) setSiteSettingsLoading(false);
    }
  }, [readSiteSettingsCache, writeSiteSettingsCache]);

  // Signed-out visitors can't read /site-settings, so they get the public
  // subset instead -- otherwise guest-facing gates (the commercial track)
  // always read "not loaded" and stay shut whatever the admin toggle says.
  // A failed fetch leaves {} (everything off, as before). Dropped if someone
  // signed in while it was in flight.
  const loadPublicSiteSettings = useCallback(async () => {
    let publicSettings = {};
    try {
      publicSettings = (await readApiJson('/public-site-settings')) || {};
    } catch {
      // Fall through with {}.
    }
    if (preparedUserId.current === null) setSiteSettings(publicSettings);
  }, []);

  const synchronizeHistory = useCallback(async (userId) => {
    setSyncStatus('syncing');
    setSyncError(null);

    try {
      // Download first, so the account's runs (from the app or another
      // browser) reach this one even if an upload below fails; then send
      // only the runs the account doesn't have yet.
      const remote = await fetchAttempts(userId);
      if (remote.error) throw remote.error;
      const onServer = new Set(remote.attempts.map((attempt) => String(attempt.id)));
      const missing = getHistory().filter((attempt) => !onServer.has(String(attempt.id)));
      mergeHistory(remote.attempts);

      const upload = await syncAttempts(missing, userId);
      if (upload.error) throw upload.error;
      if (upload.rejected?.length) {
        console.warn('Runs the server did not accept:', upload.rejected);
      }
      setSyncStatus('synced');
    } catch (error) {
      setSyncStatus('unavailable');
      setSyncError(error.message || 'Cloud synchronization is unavailable.');
    }
  }, []);

  const prepareUser = useCallback(async (currentUser) => {
    if (!currentUser) {
      setProfile(null);
      setAccessState(null);
      setSiteSettings(null);
      setIsAdmin(false);
      setProfileLoading(false);
      setAccessLoading(false);
      setSiteSettingsLoading(false);
      setRecoveryMode(false);
      setSyncStatus('idle');
      setSyncError(null);
      lastSyncedUserId.current = null;
      lastProfileUserId.current = null;
      lastAccessUserId.current = null;
      lastSiteSettingsUserId.current = null;
      preparedUserId.current = null;
      setActiveStorageUser(null);
      void loadPublicSiteSettings();
      return;
    }

    const userChanged = preparedUserId.current !== currentUser.id;
    preparedUserId.current = currentUser.id;
    if (userChanged) {
      const cached = readProfileCache(currentUser.id);
      setProfile({
        ...defaultProfile(currentUser),
        ...(cached?.profile || {})
      });
      setIsAdmin(cached ? Boolean(cached.isAdmin) || localAdminFallback(currentUser) : localAdminFallback(currentUser));
      const cachedAccess = readAccessCache(currentUser.id);
      setAccessState(cachedAccess || null);
      const cachedSiteSettings = readSiteSettingsCache();
      setSiteSettings(cachedSiteSettings || null);
      setActiveStorageUser(currentUser.id);
      // Anything practiced anonymously in this browser (including a
      // free-tier-locked module reached via a guest practice route) becomes
      // part of this account's own history, not orphaned under the 'guest'
      // scope -- must run before synchronizeHistory below so the migrated
      // attempts are included in what gets uploaded.
      migrateGuestHistoryToUser();
      // A visitor who built a plan on the public preparation-plan builder
      // before signing up gets those choices saved to their new account.
      // Awaited (not fire-and-forget) so it lands before loadBackendProfile
      // below, which would otherwise cache the pre-save preferences.
      const pendingPlanInputs = takeGuestPreparationPlanInputs();
      if (pendingPlanInputs) {
        try {
          await readApiJson('/account/preparation-preferences', {
            method: 'POST',
            body: JSON.stringify(normalizePreparationPreferences(pendingPlanInputs, {}))
          });
          // The dashboard reads the date from the assessment booking, so
          // create one too -- otherwise the new account is asked for the
          // date it just saved. No existing booking is known yet here; for
          // a brand-new account there isn't one.
          const bookingAction = planBookingAction(pendingPlanInputs);
          if (bookingAction) {
            await readApiJson('/account/assessment-booking', {
              method: 'POST',
              body: JSON.stringify(bookingAction.booking)
            });
          }
        } catch (error) {
          console.warn('Could not save your preparation plan to your account:', error.message);
        }
      }
    }
    if (userChanged && lastProfileUserId.current !== currentUser.id) {
      void loadBackendProfile(currentUser);
    }
    if (userChanged && lastAccessUserId.current !== currentUser.id) {
      void loadAccessState(currentUser);
    }
    if (lastSiteSettingsUserId.current !== currentUser.id) {
      void loadSiteSettings(currentUser, {
        force: true,
        background: !userChanged
      });
    }

    if (lastSyncedUserId.current !== currentUser.id) {
      lastSyncedUserId.current = currentUser.id;
      void synchronizeHistory(currentUser.id);
    }
  }, [loadAccessState, loadBackendProfile, loadPublicSiteSettings, loadSiteSettings, readAccessCache, readProfileCache, readSiteSettingsCache, setRecoveryMode, synchronizeHistory]);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setLoading(false);
      return undefined;
    }

    let active = true;

    supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setLoading(false);
        return;
      }

      setSession(data.session);
      setUser(data.session?.user || null);
      if (urlIndicatesRecovery()) {
        setRecoveryMode(true);
      }
      prepareUser(data.session?.user || null);
      setLoading(false);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active) return;
      const recoveryEvent = event === 'PASSWORD_RECOVERY' || urlIndicatesRecovery();
      if (recoveryEvent) {
        setRecoveryMode(true);
        const resetPath = `${window.location.origin}/auth/reset-password`;
        if (window.location.pathname !== '/auth/reset-password') {
          window.location.replace(resetPath);
          return;
        }
      } else if (event === 'SIGNED_OUT') {
        setRecoveryMode(false);
      }
      setSession(nextSession);
      setUser(nextSession?.user || null);
      prepareUser(nextSession?.user || null);
      setLoading(false);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [prepareUser, setRecoveryMode]);

  useEffect(() => {
    if (!user?.id || !session?.access_token) return undefined;
    let lastReportedAt = 0;

    const recordActivity = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastReportedAt < 60 * 1000) return;
      lastReportedAt = now;
      void apiFetch('/account/activity', { method: 'POST' }).catch(() => {
        // Activity reporting must never interrupt training or authentication.
      });
    };

    const recordVisibleActivity = () => {
      if (document.visibilityState === 'visible') recordActivity();
    };

    recordActivity();
    const heartbeat = window.setInterval(recordActivity, ACTIVITY_HEARTBEAT_MS);
    window.addEventListener('focus', recordActivity);
    document.addEventListener('visibilitychange', recordVisibleActivity);

    return () => {
      window.clearInterval(heartbeat);
      window.removeEventListener('focus', recordActivity);
      document.removeEventListener('visibilitychange', recordVisibleActivity);
    };
  }, [session?.access_token, user?.id]);

  useEffect(() => {
    if (!user?.id || !session?.access_token) return undefined;

    const refreshTimer = window.setInterval(() => {
      void loadSiteSettings(user, { force: true, background: true });
    }, SITE_SETTINGS_CACHE_TTL_MS);

    return () => window.clearInterval(refreshTimer);
  }, [loadSiteSettings, session?.access_token, user]);

  const signUp = useCallback(async ({ email, password, displayName, acceptedTermsPrivacy }) => {
    if (!isSupabaseConfigured || !supabase) {
      return { data: null, error: configurationError() };
    }

    try {
      const data = await readApiJson('/auth/signup', {
        method: 'POST',
        body: JSON.stringify({
          email,
          password,
          display_name: displayName,
          email_redirect_to: authRedirect('/auth/verify'),
          accepted_terms_privacy: acceptedTermsPrivacy === true
        })
      });
      // Fires once, right when the account row is actually created --
      // not gated behind email confirmation, since that would undercount
      // and delay the signal Smart Bidding needs. Guarded because gtag is
      // loaded async by the script tag in index.html and can be missing
      // entirely if a content/ad blocker strips it.
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-18384570058/cdf3CNT0hv8cEMqNub5E',
          value: 1.0,
          currency: 'CAD'
        });
      }
      if (data?.session?.access_token && data?.session?.refresh_token) {
        const sessionResult = await supabase.auth.setSession(data.session);
        if (sessionResult.error) return sessionResult;
        return sessionResult;
      }
      return { data: { user: data?.user || null, session: null }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, []);

  const signIn = useCallback(async ({ email, password }) => {
    if (!isSupabaseConfigured || !supabase) {
      return { data: null, error: configurationError() };
    }
    try {
      const data = await readApiJson('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      if (!data?.session?.access_token || !data?.session?.refresh_token) {
        return { data: null, error: { message: 'Login did not return a valid session.' } };
      }
      return supabase.auth.setSession(data.session);
    } catch (error) {
      return { data: null, error };
    }
  }, []);

  const signOut = useCallback(async () => {
    if (!isSupabaseConfigured || !supabase) {
      return { error: configurationError() };
    }
    if (user?.id) {
      sessionStorage.removeItem(`${PROFILE_CACHE_PREFIX}:${user.id}`);
      sessionStorage.removeItem(`${ACCESS_CACHE_PREFIX}:${user.id}`);
      sessionStorage.removeItem(SITE_SETTINGS_CACHE_KEY);
    }
    localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
    const result = await supabase.auth.signOut();
    setActiveStorageUser(null);
    setAccessState(null);
    setSiteSettings(null);
    setRecoveryMode(false);
    return result;
  }, [setRecoveryMode, user?.id]);

  const resendConfirmation = useCallback(async (email) => {
    if (!isSupabaseConfigured || !supabase) {
      return { data: null, error: configurationError() };
    }
    try {
      const data = await readApiJson('/auth/resend', {
        method: 'POST',
        body: JSON.stringify({
          email,
          redirect_to: authRedirect('/auth/verify')
        })
      });
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    if (!isSupabaseConfigured || !supabase) {
      return { data: null, error: configurationError() };
    }
    try {
      const data = await readApiJson('/auth/password-reset', {
        method: 'POST',
        body: JSON.stringify({
          email,
          redirect_to: authRedirect('/auth/reset-password')
        })
      });
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, []);

  // Exchanges a reset email's token_hash for a recovery session. Done when
  // the reader presses Continue on the reset page, never on page load, so
  // mail scanners that open links before the reader does (Microsoft 365 Safe
  // Links, for one) can't use the one-time token up.
  const verifyRecoveryLink = useCallback(async (tokenHash) => {
    if (!isSupabaseConfigured || !supabase) {
      return { data: null, error: configurationError() };
    }
    const { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' });
    if (error) return { data: null, error };
    setRecoveryMode(true);
    return { data, error: null };
  }, [setRecoveryMode]);

  const updatePassword = useCallback(async (password) => {
    if (!isSupabaseConfigured || !supabase) {
      return { data: null, error: configurationError() };
    }
    try {
      const data = await readApiJson('/auth/password-update', {
        method: 'POST',
        body: JSON.stringify({ password })
      });
      setRecoveryMode(false);
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, [setRecoveryMode]);

  const retrySync = useCallback(() => {
    if (user?.id) void synchronizeHistory(user.id);
  }, [synchronizeHistory, user]);

  const refreshProfile = useCallback(() => {
    if (user) {
      sessionStorage.removeItem(`${PROFILE_CACHE_PREFIX}:${user.id}`);
      void loadBackendProfile(user, { force: true });
    }
  }, [loadBackendProfile, user]);

  const updateTrainingProfile = useCallback(async ({ serviceProfile, targetRole }) => {
    try {
      const data = await readApiJson('/account/training-profile', {
        method: 'POST',
        body: JSON.stringify({ service_profile: serviceProfile ?? null, target_role: targetRole ?? null })
      });
      // Must be awaited, not fire-and-forget: callers navigate or let the
      // user refresh right after this resolves. If the sessionStorage
      // profile cache isn't rewritten before that happens, the next page
      // load reads the (just-cleared) stale cache instead of the network,
      // and the saved profile appears to have been lost.
      if (user) {
        sessionStorage.removeItem(`${PROFILE_CACHE_PREFIX}:${user.id}`);
        await loadBackendProfile(user, { force: true });
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, [loadBackendProfile, user]);

  // The Terms acceptance window's button.
  const acceptTermsPrivacy = useCallback(async () => {
    try {
      const data = await readApiJson('/account/terms-acceptance', { method: 'POST' });
      if (user) {
        sessionStorage.removeItem(`${PROFILE_CACHE_PREFIX}:${user.id}`);
        await loadBackendProfile(user, { force: true });
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, [loadBackendProfile, user]);

  const updatePreparationPreferences = useCallback(async (preferences) => {
    try {
      const data = await readApiJson('/account/preparation-preferences', {
        method: 'POST',
        body: JSON.stringify(normalizePreparationPreferences(preferences, profile || {}))
      });
      if (user) {
        sessionStorage.removeItem(`${PROFILE_CACHE_PREFIX}:${user.id}`);
        await loadBackendProfile(user, { force: true });
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, [loadBackendProfile, profile, user]);

  const updateAssessmentBooking = useCallback(async (booking) => {
    try {
      const data = await readApiJson('/account/assessment-booking', {
        method: 'POST',
        body: JSON.stringify(booking)
      });
      if (user) {
        sessionStorage.removeItem(`${PROFILE_CACHE_PREFIX}:${user.id}`);
        await loadBackendProfile(user, { force: true });
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, [loadBackendProfile, user]);

  const setAssessmentBookingStatus = useCallback(async (statusUpdate) => {
    try {
      const data = await readApiJson('/account/assessment-booking/status', {
        method: 'POST',
        body: JSON.stringify(statusUpdate)
      });
      if (user) {
        sessionStorage.removeItem(`${PROFILE_CACHE_PREFIX}:${user.id}`);
        await loadBackendProfile(user, { force: true });
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, [loadBackendProfile, user]);

  const saveAssessmentResult = useCallback(async (result) => {
    try {
      const data = await readApiJson('/account/assessment-results', {
        method: 'POST',
        body: JSON.stringify(result)
      });
      if (user) {
        sessionStorage.removeItem(`${PROFILE_CACHE_PREFIX}:${user.id}`);
        await loadBackendProfile(user, { force: true });
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, [loadBackendProfile, user]);

  const deleteAssessmentResult = useCallback(async (bookingId) => {
    try {
      const data = await readApiJson(`/account/assessment-results/${encodeURIComponent(bookingId)}`, {
        method: 'DELETE'
      });
      if (user) {
        sessionStorage.removeItem(`${PROFILE_CACHE_PREFIX}:${user.id}`);
        await loadBackendProfile(user, { force: true });
      }
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }, [loadBackendProfile, user]);

  const refreshAccess = useCallback(() => {
    if (user) {
      sessionStorage.removeItem(`${ACCESS_CACHE_PREFIX}:${user.id}`);
      void loadAccessState(user, { force: true });
    }
  }, [loadAccessState, user]);

  const refreshSiteSettings = useCallback(() => {
    if (user) {
      sessionStorage.removeItem(SITE_SETTINGS_CACHE_KEY);
      void loadSiteSettings(user, { force: true, background: true });
    }
  }, [loadSiteSettings, user]);

  const fullAccess = evaluateFullAccess(accessState, isAdmin, siteSettings, profile?.preparation_preferences?.active_family);

  useEffect(() => {
    try {
      if (user && fullAccess) window.localStorage.setItem(FULL_ACCESS_HINT_KEY, '1');
      else if (!loading) window.localStorage.removeItem(FULL_ACCESS_HINT_KEY);
    } catch {
      // Storage unavailable: the static footer keeps its Pricing link.
    }
  }, [fullAccess, loading, user]);

  const value = useMemo(() => ({
    session,
    user,
    profile,
    loading,
    profileLoading,
    accessState,
    accessLoading,
    siteSettings,
    siteSettingsLoading,
    hasFullAccess: fullAccess,
    isAdmin,
    isRecoveryMode,
    syncStatus,
    syncError,
    isConfigured: isSupabaseConfigured,
    signUp,
    signIn,
    signOut,
    resendConfirmation,
    requestPasswordReset,
    verifyRecoveryLink,
    updatePassword,
    retrySync,
    refreshProfile,
    refreshAccess,
    refreshSiteSettings,
    updateTrainingProfile,
    acceptTermsPrivacy,
    updatePreparationPreferences,
    updateAssessmentBooking,
    setAssessmentBookingStatus,
    saveAssessmentResult,
    deleteAssessmentResult
  }), [
    session,
    user,
    profile,
    fullAccess,
    loading,
    profileLoading,
    accessState,
    accessLoading,
    siteSettings,
    siteSettingsLoading,
    isAdmin,
    isRecoveryMode,
    syncStatus,
    syncError,
    signUp,
    signIn,
    signOut,
    resendConfirmation,
    requestPasswordReset,
    verifyRecoveryLink,
    updatePassword,
    retrySync,
    refreshProfile,
    refreshAccess,
    refreshSiteSettings,
    updateTrainingProfile,
    acceptTermsPrivacy,
    updatePreparationPreferences,
    updateAssessmentBooking,
    setAssessmentBookingStatus,
    saveAssessmentResult,
    deleteAssessmentResult
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
};
